/**
 * Descriptor-driven Power Distribution Summary source (#997 — Story 4.4).
 *
 * Reproduces the bespoke `getPowerDistributionEditorSummaryDefinitions()` +
 * `getPowerDistributionNoteSummaryDefinitions()` row sets and per-unit value
 * / title projection from the Power Distribution descriptor + the shared
 * Summary factory, byte-identically against the Story 4.4 golden masters
 * (`power-distribution-summary-rows.json`,
 * `power-distribution-summary-filled.json`). This is the engine-driven
 * replacement Story 4.4 re-points the Summary and the golden tests at — same
 * oracle, new implementation.
 *
 * Row-set layout mirrors what the bespoke code produced from
 * `requestFieldRegistry.ts`:
 *
 *   - Per-unit editor group (`subsectionId: 'powerDistribution'`): `name`,
 *     `status`, `powerUpsOutputVoltage`, `powerDistributionVoltageToLoad`,
 *     `powerOverheadBuswayOrUnderFloor`, `powerBuswayAmperage`,
 *     `powerBuswayLengthFt`, `powerTapOfBoxRequirements` — the `subsection`
 *     marker stamps on the first row (via `buildEditorSubsectionSummaryRows`).
 *   - Per-unit note group (`subsectionId: 'additionalInformation'`):
 *     `powerAdditionalInformation` (labelOverride "Additional Power
 *     Distribution Info") — marker stamps here.
 *
 * Power Distribution has NO request-level shared field (contrast Liquid
 * Cooling's `lcLiquidToChipDesired`; mirrors Large UPS), so `sharedFieldKeys`
 * is empty on the descriptor and there is no shared-row accessor here.
 *
 * The identity fields `name`/`status` are not registry fields, so their labels
 * fall back to the editor-slice `shortLabel` values ("Name" / "Type") supplied
 * by `POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS`. No `labelOverrides` are
 * needed for them (the fallback is already correct); the ONLY label override
 * is the additional-info note label (`Additional Power Distribution Info`),
 * matching bespoke.
 */

import { POWER_DISTRIBUTION_DESCRIPTOR } from './powerDistribution.descriptor';
import { repeatableInstanceNameTypeRowValue } from '../../summary/repeatableInstanceSummaryDisplay';
import { buildEditorSubsectionSummaryRows } from '../fields/projections/editorSubsectionSummaryRows';
import { facilityPowerSummaryRowValue } from '../fields/projections/summaryRowValues';
import {
  FACILITY_POWER_NOTES_SUBSECTION_FIELDS,
  POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
} from '../fields/requestFieldRegistry';
import { FACILITY_POWER_FIELDS } from '../fields/steps/facility-power.fields';

import type { SummaryCardRow } from '../../summary/summaryCardSourceTypes';
import type { PowerDistributionConfiguration } from '../../types/powerDistribution';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

const RBAC_ANCHOR =
  POWER_DISTRIBUTION_DESCRIPTOR.permissionRef.rbacAnchorDataField;
const NOTE_RBAC_ANCHOR = 'powerAdditionalInformation';

/**
 * Registry field metadata (labels) the factory resolves per field. Keeping
 * this pointed at the FACILITY_POWER_FIELDS registry (rather than the
 * descriptor's `fields`) mirrors the bespoke source and keeps
 * labels/subsection metadata flowing through the same lookup the engine
 * expects.
 */
const REGISTRY_FIELDS = FACILITY_POWER_FIELDS;

/** Bespoke label override for the note field (`powerDistributionNoteSummaryRows`). */
const POWER_DISTRIBUTION_NOTE_LABEL_OVERRIDES: Readonly<
  Record<string, string>
> = {
  powerAdditionalInformation: 'Additional Power Distribution Info',
};

function buildGroup(
  editorFields: readonly SubsectionFieldDescriptor[],
  subsectionId: string,
  rbacAnchorDataField: string,
  labelOverrides?: Readonly<Record<string, string>>,
): SummaryCardRow[] {
  return buildEditorSubsectionSummaryRows({
    editorFields,
    registryFields: REGISTRY_FIELDS,
    rbacAnchorDataField,
    subsectionId,
    labelOverrides,
  });
}

/**
 * Full ordered per-unit Power Distribution Summary row set (editor + note),
 * byte-identical to the bespoke
 * `getPerPowerDistributionSummaryDefinitions()` output (which concatenates
 * `getPowerDistributionEditorSummaryDefinitions()` +
 * `getPowerDistributionNoteSummaryDefinitions()`).
 */
export function getPowerDistributionDescriptorSummaryDefinitions(): SummaryCardRow[] {
  return [
    ...buildGroup(
      POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
      'powerDistribution',
      RBAC_ANCHOR,
    ),
    ...buildGroup(
      FACILITY_POWER_NOTES_SUBSECTION_FIELDS,
      'additionalInformation',
      NOTE_RBAC_ANCHOR,
      POWER_DISTRIBUTION_NOTE_LABEL_OVERRIDES,
    ),
  ];
}

/**
 * Per-unit Power Distribution Summary value — delegates to
 * `repeatableInstanceNameTypeRowValue` for the identity fields (`name`,
 * Existing/New `status`) and `facilityPowerSummaryRowValue` for the PD detail
 * fields (pass-through with defensive typing). Re-homed here so the Summary
 * value path no longer routes through the bespoke display module.
 */
export function powerDistributionDescriptorUnitRowValue(
  unit: PowerDistributionConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  const nameTypeValue = repeatableInstanceNameTypeRowValue(unit, dataField);
  if (nameTypeValue !== undefined) {
    return nameTypeValue;
  }
  return facilityPowerSummaryRowValue(
    unit as unknown as Record<string, unknown>,
    dataField,
  );
}

/**
 * Resolve a Power Distribution unit's display name (user-set `name` →
 * `Power Distribution N` fallback based on `displayOrder`, else `null` so the
 * caller falls back to the ordinal). Kept here so the Summary title no longer
 * depends on the bespoke display module.
 */
function resolvePowerDistributionSummaryDisplayName(
  unit: PowerDistributionConfiguration,
): string | null {
  const name = unit.name?.trim();
  if (name) {
    return name;
  }
  if (unit.displayOrder != null && unit.displayOrder > 0) {
    return `Power Distribution ${unit.displayOrder}`;
  }
  return null;
}

/**
 * Per-unit Summary card title — `Existing/New — <name>` (fallback
 * `… Power Distribution N`).
 */
export function getPowerDistributionDescriptorUnitTitle(
  unit: PowerDistributionConfiguration,
  index: number,
): string {
  const status = unit.status === 'existing' ? 'Existing' : 'New';
  const name = resolvePowerDistributionSummaryDisplayName(unit);
  return name
    ? `${status} — ${name}`
    : `${status} Power Distribution ${index + 1}`;
}
